import Big, { BigSource } from "big.js";

import { ValidationHelper } from "../Engine/src/Engine/ValidationHelper/ValidationHelper";
import { wrapDecimalOperand } from "../Engine/src/Helpers/AutocalcCommonFunctions";
import { convertDate } from "../Engine/src/Helpers/DateHelpers";

import { isNotNullOrEmpty, isNullOrWhiteSpace } from "./TypingUtils";

type ValueSelector = (path: string) => string;
type BigSourceOrUndefined = BigSource | undefined;

const isBigSource = (value: unknown): value is BigSource => {
    if (typeof value === "number" || typeof value === "string" || value instanceof Big) {
        return true;
    }
    return false;
};

const isBigOrNumber = (value: Big | number | undefined | null | unknown) =>
    typeof value === "number" || value instanceof Big;

export class KCLangRuntimeUtils {
    public static kcLangUtilsName = "kcLangUtils";

    public and(left: boolean | undefined, right: boolean | undefined): boolean | undefined {
        if (left == undefined || right == undefined) {
            return undefined;
        }
        return left && right;
    }

    public division(left: BigSourceOrUndefined, right: BigSourceOrUndefined): Big | undefined {
        if (isBigSource(left) && isBigSource(right)) {
            return wrapDecimalOperand(left).div(right);
        }
        return undefined;
    }

    public eq(left: BigSourceOrUndefined | boolean, right: BigSourceOrUndefined | boolean): boolean {
        if (
            ((typeof left === "string" || typeof left === "boolean") && typeof right === "string") ||
            typeof right === "boolean"
        ) {
            return left === right;
        }
        if (isBigSource(left) && isBigSource(right)) {
            return wrapDecimalOperand(left).cmp(right) === 0;
        }
        return false;
    }

    public ge(left: BigSourceOrUndefined, right: BigSourceOrUndefined): boolean {
        if (isBigSource(left) && isBigSource(right)) {
            return wrapDecimalOperand(left).gte(right);
        }
        return false;
    }

    public gt(left: BigSourceOrUndefined, right: BigSourceOrUndefined): boolean {
        if (isBigSource(left) && isBigSource(right)) {
            return wrapDecimalOperand(left).gt(right);
        }
        return false;
    }

    public le(left: BigSourceOrUndefined, right: BigSourceOrUndefined): boolean {
        if (isBigSource(left) && isBigSource(right)) {
            return wrapDecimalOperand(left).lte(right);
        }
        return false;
    }

    public lt(left: BigSourceOrUndefined, right: BigSourceOrUndefined): boolean {
        if (isBigSource(left) && isBigSource(right)) {
            return wrapDecimalOperand(left).lt(right);
        }
        return false;
    }

    public minus(argument: BigSourceOrUndefined): unknown {
        if (isBigSource(argument)) {
            return wrapDecimalOperand(argument).mul(-1);
        }
        return undefined;
    }

    public multiply(left: BigSourceOrUndefined, right: BigSourceOrUndefined): unknown {
        if (isBigSource(left) && isBigSource(right)) {
            return wrapDecimalOperand(left).mul(right);
        }
        return undefined;
    }

    public not(left?: boolean): boolean | undefined {
        return left != undefined ? !left : undefined;
    }

    public noteq(left: BigSourceOrUndefined, right: BigSourceOrUndefined): boolean {
        return !this.eq(left, right);
    }

    public or(left: boolean | undefined, right: boolean | undefined): boolean | undefined {
        if (typeof left == "boolean" || typeof right == "boolean") {
            return left || right;
        }
        return undefined;
    }

    public round(value: BigSourceOrUndefined, fractionDigits?: number): Big | undefined {
        if (isBigSource(value)) {
            return wrapDecimalOperand(value).round(fractionDigits);
        }
        return undefined;
    }

    public floor(value: BigSourceOrUndefined): Big | undefined {
        if (isBigSource(value)) {
            return wrapDecimalOperand(wrapDecimalOperand(value).round(undefined, 0));
        }
        return undefined;
    }

    public getYear(value: string): number | undefined {
        return new Date(value)?.getFullYear();
    }

    public dateTime(value: string): Date | undefined {
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return date;
    }

    public sum(left: BigSourceOrUndefined, right: BigSourceOrUndefined): Big | undefined {
        if (left == undefined && right == undefined) {
            return undefined;
        }

        if (isBigSource(left) && isBigSource(right)) {
            return wrapDecimalOperand(left).add(right);
        }

        if (isBigSource(left)) {
            return wrapDecimalOperand(left);
        }

        if (isBigSource(right)) {
            return wrapDecimalOperand(right);
        }

        return undefined;
    }

    public abs(argument: BigSourceOrUndefined): Big | undefined {
        if (isBigSource(argument)) {
            return wrapDecimalOperand(argument).abs();
        }
        return undefined;
    }

    public xabs(argument: BigSourceOrUndefined): Big | undefined {
        if (isBigSource(argument)) {
            const value = wrapDecimalOperand(argument);
            return value.lt(0) ? new Big(0) : value;
        }
        return undefined;
    }

    public length(argument: string | undefined | null): number | undefined {
        if (typeof argument === "string") {
            return argument.length;
        }
        return undefined;
    }

    public exists(valueSelector: (path: string) => unknown, path: string): boolean | undefined {
        if (valueSelector == undefined) {
            return undefined;
        }
        const value = valueSelector(path);
        return value != undefined && value != "";
    }

    public isSnils(valueSelector: ValueSelector, path: string): boolean | undefined {
        if (valueSelector == undefined) {
            return undefined;
        }
        const value = valueSelector(path);
        const normalizedSnils = value?.replace(/[^0-9]/g, "");
        if (isNullOrWhiteSpace(normalizedSnils)) {
            return false;
        }
        const result = ValidationHelper.isSnils(normalizedSnils);

        return result;
    }

    public isRegNumSfr(valueSelector: ValueSelector, path: string): boolean | undefined {
        if (valueSelector == undefined) {
            return undefined;
        }
        const value = valueSelector(path);
        const normalizedRegNum = value?.replace(/[^0-9]/g, "");
        if (isNullOrWhiteSpace(normalizedRegNum)) {
            return false;
        }
        const result = ValidationHelper.isRegNumSfr(normalizedRegNum);

        return result;
    }

    public isInn(valueSelector: ValueSelector, path: string): boolean | undefined {
        if (valueSelector == undefined) {
            return undefined;
        }
        const value = valueSelector(path);
        const normalizedSnils = value?.replace(/[^0-9]/g, "");
        if (isNullOrWhiteSpace(normalizedSnils)) {
            return false;
        }
        const result = ValidationHelper.isInn(normalizedSnils);

        return result;
    }

    public isOgrn(valueSelector: ValueSelector, path: string): boolean | undefined {
        if (valueSelector == undefined) {
            return undefined;
        }
        const value = valueSelector(path);
        const normalizedOgrn = value?.replace(/[^0-9]/g, "");
        if (isNullOrWhiteSpace(normalizedOgrn)) {
            return false;
        }
        const result = ValidationHelper.isOgrn(normalizedOgrn);

        return result;
    }

    public isValidAccountNumber(valueSelector: ValueSelector, bik: string, account: string): boolean | undefined {
        if (valueSelector == undefined) {
            return undefined;
        }
        const bikValue = valueSelector(bik);
        const accountValue = valueSelector(account);
        if (isNullOrWhiteSpace(bikValue) || isNullOrWhiteSpace(accountValue)) {
            return false;
        }
        const result = ValidationHelper.isValidAccountNumber(bikValue, accountValue);
        return result;
    }

    public isValidMirCardNumber(valueSelector: ValueSelector, path: string): boolean | undefined {
        if (valueSelector == undefined) {
            return undefined;
        }
        const value = valueSelector(path);
        if (isNullOrWhiteSpace(value)) {
            return false;
        }
        const result = ValidationHelper.isValidMirCardNumber(value);

        return result;
    }

    public convertToDecimal(argument: BigSourceOrUndefined | string | undefined): Big | undefined {
        return wrapDecimalOperand(argument);
    }

    public convertToString(argument: boolean | BigSourceOrUndefined | undefined | null): string | undefined {
        if (typeof argument === "string") {
            return argument;
        }

        if (typeof argument === "number") {
            return argument.toString();
        }

        if (typeof argument === "boolean") {
            return argument.toString();
        }

        if (argument instanceof Big) {
            return argument.toString();
        }

        return undefined;
    }

    public regexMatch(pattern: string | undefined, value: string | undefined): boolean | undefined {
        if (pattern == undefined || value == undefined) {
            return undefined;
        }
        if (typeof value === "string") {
            return new RegExp(pattern).test(value);
        }
        return undefined;
    }

    public regexReplace(
        value: string | undefined,
        pattern: string | undefined,
        replacement: string | undefined,
        flags?: string
    ): string | undefined {
        if (pattern == undefined || value == undefined || replacement == undefined || typeof value !== "string") {
            return undefined;
        }

        return value.replace(new RegExp(pattern, flags), replacement);
    }

    public oneof(values: Array<string | number | Big>, value: string | number | Big): boolean | undefined {
        if (values == undefined) {
            return undefined;
        }
        if (typeof value === "string") {
            return values.indexOf(value) !== -1;
        }

        if (isBigOrNumber(value)) {
            const valueAsBig = new Big(value);
            return values.some(item => isBigOrNumber(item) && valueAsBig.eq(item));
        }

        return false;
    }

    public substring(
        str: string | undefined,
        start: number | undefined,
        length: number | undefined
    ): string | undefined {
        if (str == undefined || start == undefined || length == undefined) {
            return undefined;
        }
        if (length <= 0) {
            return "";
        }
        const end = start + length;

        return str.substring(start, end);
    }

    public dateNow(): string | undefined {
        return new Date().toLocaleDateString("ru-RU");
    }

    public numerator(argument: string): Big | undefined {
        if (!argument) {
            return new Big(0);
        }

        return wrapDecimalOperand(argument.split("/")[0]) ?? new Big(0);
    }

    public denominator(argument: string): Big | undefined {
        if (!argument) {
            return new Big(0);
        }

        const denominatorVal = argument.split("/")[1];

        if (denominatorVal === undefined) {
            return new Big(1);
        }

        return wrapDecimalOperand(denominatorVal) ?? new Big(0);
    }

    public uuid(): string | undefined {
        return self.crypto.randomUUID();
    }
}
